/**
 * Ask-AI source pane (S9) — single-tile flow: prompt -> staged progress ->
 * ONE proposal (title, suggested tile type, collapsible SQL review) -> "Use
 * this" hands the proposal to the dialog (`AddTileDialog` sets the SQL
 * buffer + tile type + title, switches to the SQL tab, and auto-runs the
 * preview — see its `onApply` wiring). Nothing here ever runs SQL: drafting
 * only calls the AI; the preview (always read-only, S3 policy) happens after
 * the user applies.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { DatabaseEngineId } from '@/core/engines';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { useDashboardAiPropose } from '../../hooks/useDashboardAiPropose';
import type { TileProposal } from '../../services/aiTileProposal';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

const TILE_TYPE_LABEL: Record<TileProposal['tileType'], string> = {
  chart: 'chart',
  stat: 'stat',
  table: 'table',
  pivot: 'pivot',
};

export function AiSourcePane({
  connectionId,
  onApply,
}: {
  connectionId: string;
  onApply: (proposal: TileProposal) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const { state, propose, reset } = useDashboardAiPropose();
  const active = useConnectionStore((s) => s.activeConnections).find((c) => c.id === connectionId);
  const engineId: DatabaseEngineId = (active?.connection.engine as DatabaseEngineId) ?? 'mssql';

  const draft = () => {
    if (!connectionId || !prompt.trim()) return;
    void propose({
      connectionId,
      connectionAlias: safeConnectionName(active?.connection, connectionId),
      engine: engineId,
      database: active?.connection.database,
      prompt: prompt.trim(),
      mode: 'single',
    });
  };

  const proposal = state.status === 'done' ? state.proposals[0] : null;

  return (
    <div className="flex min-h-0 flex-col gap-2" data-testid="ai-source-pane">
      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Describe the tile you want — e.g. Top return reasons over the last 28 days"
        rows={3}
        className="text-xs"
        disabled={state.status === 'running'}
        data-testid="ai-source-prompt"
      />
      {/* WHY tooltip on a span wrapper: the Button's disabled styles set
          pointer-events-none, so a `title` on the button itself never shows
          while disabled (S24 F19/F41). */}
      <span
        className="w-fit"
        title={!connectionId ? 'Pick a connection first' : !prompt.trim() ? 'Describe the tile first' : undefined}
      >
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-7 w-fit gap-1.5 text-violet-600 dark:text-violet-400"
          onClick={draft}
          disabled={state.status === 'running' || !connectionId || !prompt.trim()}
          data-testid="ai-source-draft-button"
        >
          {state.status === 'running' ? <RandomDotmSpinner size={14} /> : <Sparkles className="h-3.5 w-3.5" />}
          Draft tile
        </Button>
      </span>

      {state.status === 'running' && (
        <div className="flex flex-col gap-1 text-xs text-muted-foreground" data-testid="ai-source-progress">
          <span data-state={state.step === 'schema' ? 'doing' : 'done'}>
            {state.step === 'schema' ? 'Inspecting schema…' : 'Inspecting schema ✓'}
          </span>
          <span data-state={state.step === 'drafting' ? 'doing' : 'idle'}>Drafting query…</span>
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex flex-col gap-1.5 text-xs text-destructive" data-testid="ai-source-error">
          <span>{state.message}</span>
          <Button type="button" size="sm" variant="outline" className="h-6 w-fit text-xs" onClick={reset}>
            Try again
          </Button>
        </div>
      )}

      {proposal && (
        <div className="rounded-md border border-border p-2.5" data-testid="ai-source-proposal">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <span>{proposal.title}</span>
            <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {TILE_TYPE_LABEL[proposal.tileType]}
            </span>
          </div>
          <details className="mt-1.5">
            <summary
              className="cursor-pointer text-[11px] text-muted-foreground"
              data-testid="ai-source-review-toggle"
            >
              Review SQL
            </summary>
            <pre
              className="mt-1.5 overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[10.5px] text-muted-foreground"
              data-testid="ai-source-proposal-sql"
            >
              {proposal.sql}
            </pre>
          </details>
          <Button
            type="button"
            size="sm"
            className="mt-2 h-6 text-xs"
            onClick={() => {
              // No analytics here (S16 adjudication): this only stages the
              // SQL into the dialog — the user can still Cancel, so it
              // doesn't represent a persisted tile. `dashboard_tile_add
              // {source:'ai'}` fires from useDashboardActions.submitTile on
              // the ACTUAL add, which is the correct success-side signal for
              // this surface. (`dashboard_ai_add` stays reserved for the
              // Build-with-AI panel's direct multi-proposal adds, which
              // don't go through submitTile at all.)
              onApply(proposal);
            }}
            data-testid="ai-source-use-button"
          >
            Use this
          </Button>
        </div>
      )}

      <span className="text-[11px] text-muted-foreground">
        Read-only — you review the SQL and the preview before the tile is added.
      </span>
    </div>
  );
}
